import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import AdminTable from 'app/components/admin/AdminTable';
import {
  Box,
  Button,
  Flex,
  FlexColumn,
  Popover,
  Spinner,
  Tag,
  Text,
  Tooltip,
  Icon,
  showErrorToast
} from 'core/components';
import { Collection } from 'core/model';
import { RemoveButton } from 'core/form/components';
import { dateTimeFormatter } from 'core/util/dateUtils';
import { FiCopy, FiRefreshCcw, FiUsers } from 'react-icons/fi';
import KentikLogo from 'app/components/KentikLogo';
import { Position } from '@blueprintjs/core';
import { CELL_TYPES, NewButton } from 'core/components/table';
import Search from 'core/components/table/Search';
import Heading from 'core/components/Heading';
import storeLoader from 'app/stores/storeLoader';
import $auth from 'app/stores/$auth';
import RunbookFormDialog from 'app/views/settings/ai/RunbookFormDialog';
import RunbookHistoryDialog from 'app/views/settings/ai/RunbookHistoryDialog';
import SettingsPanelHeading from 'app/views/settings/ai/SettingsPanelHeading';

const ACTIVE_ALERT_LIMIT = 2000;

@inject('$alerting', '$auth', '$investigationConfig')
@storeLoader('$runbook')
@observer
class RunbookTable extends Component {
  state = {
    isEditDialogOpen: false,
    isHistoryDialogOpen: false,
    isAddDialogOpen: false,
    selectedModel: undefined,
    policies: undefined,
    searchTerm: '',
    searchFromUrl: false
  };

  /**
   * Sets uiProperties.activeAlerts on each local (independent) policy model so AdminTable
   * can sort by it. These are the same instances that were in policyCollection at fetch time —
   * a subsequent policyCollection.fetch() creates brand-new instances, so these are never
   * clobbered by a concurrent re-fetch.
   */
  attachAlertsToPolicies = (alertCollection, localPolicies) => {
    // Clear first so re-runs start clean
    localPolicies.each((policy) => {
      policy.set('uiProperties', { ...policy.get('uiProperties'), activeAlerts: [] });
    });

    alertCollection.each((alert) => {
      // Alerts reference policies by ruleId (rule_id), not by id
      const { ruleId } = alert;
      if (!ruleId) {
        return;
      }
      // Find policy by ruleId since collection.get() looks up by id field
      const policy = localPolicies.get().find((p) => p.ruleId === ruleId);
      if (!policy) {
        return;
      }
      const uiProperties = policy.get('uiProperties') || {};
      policy.set('uiProperties', { ...uiProperties, activeAlerts: [...(uiProperties.activeAlerts || []), alert] });
    });
  };

  getSearchTermFromQuery() {
    const { location } = this.props;
    const params = new URLSearchParams(location?.search);
    return params.get('search') || '';
  }

  clearSearchQuery() {
    const { history, location } = this.props;
    const params = new URLSearchParams(location?.search);
    if (params.has('search')) {
      params.delete('search');
      history.replace({ ...location, search: params.toString() });
    }
  }

  componentDidMount() {
    const { $alerting, $investigationConfig } = this.props;
    const { collection, policyCollection, policyTemplateCollection } = $alerting;
    const searchTerm = this.getSearchTermFromQuery();
    const canReadPolicies = $auth.isAdministrator || $auth.hasRbacPermissions(['alerting.policies::read']);
    const canReadAlerts = $auth.isAdministrator || $auth.hasRbacPermissions(['alerting.alerts::read']);

    if (searchTerm) {
      this.setState({ searchTerm, searchFromUrl: true });
    }

    // $investigationConfig.collection.fetch short-circuits without the aiInvestigation permission.
    if ($auth.isAdministrator && this.canDeleteRunbooks) {
      $investigationConfig.collection
        .fetch({ include_investigations: false, skipPrefetch: true })
        .catch((err) => showErrorToast(`Failed to load auto investigation assignments: ${err.message}`));
    }

    if (!canReadPolicies) {
      this.setState({ policies: new Collection([], { hasFetched: true }) });
      return;
    }

    if ($auth.isPresetCompany) {
      policyTemplateCollection
        .fetch()
        .then(() => {
          const policies = new Collection(policyTemplateCollection.get(), { hasFetched: true });
          this.setState({ policies });
        })
        .catch(() => {});
      return;
    }

    if (canReadAlerts) {
      collection.resetState();
    }

    const activeAlertsRequest = canReadAlerts
      ? collection.setServerFilter(
          { applications: [] },
          { force: true },
          { data: { limit: ACTIVE_ALERT_LIMIT, pagination: { includeTotalCount: true } } }
        )
      : Promise.resolve();

    Promise.all([
      policyTemplateCollection.fetch(),
      policyCollection.fetch().then(() => new Collection(policyCollection.get(), { hasFetched: true })),
      activeAlertsRequest
    ])
      .then(([, policies]) => {
        if (canReadAlerts) {
          this.attachAlertsToPolicies(collection, policies);
        }
        this.setState({ policies });
      })
      .catch(() => {});
  }

  componentDidUpdate(prevProps) {
    const { $runbook, loading: storeLoading } = this.props;

    // Sync search term from URL into state (handles same-page navigation)
    const incoming = this.getSearchTermFromQuery();
    const prevIncoming = new URLSearchParams(prevProps.location?.search).get('search') || '';
    if (incoming && incoming !== prevIncoming) {
      this.setState({ searchTerm: incoming, searchFromUrl: true });
    }

    const { searchTerm, searchFromUrl } = this.state;
    if (searchTerm && !storeLoading && $runbook.collection.filterState !== searchTerm) {
      // Only re-fetch when navigating via URL (e.g. "View Runbook") and the runbook
      // isn't in the collection yet. Don't re-fetch for user-typed searches.
      if (searchFromUrl && $runbook.collection.hasFetched) {
        const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
        const hasMatch = $runbook.collection.unfiltered.some((m) => regex.test(m.get('name') || ''));

        if (!hasMatch) {
          this.setState({ searchFromUrl: false });
          $runbook.collection.fetch({ force: true }).then(() => {
            this.clearSearchQuery();
            $runbook.collection.filter(searchTerm);
          });
          return;
        }
      }

      this.setState({ searchFromUrl: false });
      this.clearSearchQuery();
      $runbook.collection.filter(searchTerm);
    }
  }

  get canCreateRunbooks() {
    const { $runbook } = this.props;
    return $runbook.canCreateRunbooks;
  }

  get canUpdateRunbooks() {
    const { $runbook } = this.props;
    return $runbook.canUpdateRunbooks;
  }

  get canDeleteRunbooks() {
    const { $runbook } = this.props;
    return $runbook.canDeleteRunbooks;
  }

  get columns() {
    const { $runbook } = this.props;
    const canCreate = this.canCreateRunbooks;
    const canUpdate = this.canUpdateRunbooks;
    const canDelete = this.canDeleteRunbooks;

    const cellActions = {
      type: CELL_TYPES.ACTION,
      actions: [
        (model) => {
          if (!canDelete || (model.isPreset && !$auth.isPresetCompany)) {
            return null;
          }

          const { $investigationConfig } = this.props;
          const mappings = model.get('mappings');
          const hasMappings = mappings && Array.isArray(mappings) && mappings.length > 0;

          let linkedConfigsConfirmBodyContent;
          if (hasMappings) {
            const runbookId = String(model.get('id'));
            const linkedConfigs = $investigationConfig.collection.models.filter((config) => {
              const runbookIds = config.get('runbook_ids') || [];
              return runbookIds.includes(runbookId);
            });
            const linkedConfigNames = linkedConfigs.map((config) => config.get('policy')?.name).filter(Boolean);
            const hasLinkedConfigs = linkedConfigNames.length > 0;

            linkedConfigsConfirmBodyContent = hasLinkedConfigs ? (
              <FlexColumn>
                <Text>
                  This runbook is used by {linkedConfigNames.length} auto investigation config
                  {linkedConfigNames.length > 1 ? 's' : ''}. Are you sure you want to permanently delete it?
                </Text>
                <FlexColumn mt={1}>
                  <Text small muted mb="4px">
                    Affected auto investigation configs:
                  </Text>
                  <Box as="ul" m={0} pl={3}>
                    {linkedConfigNames.map((name) => (
                      <Box as="li" key={name}>
                        <Text small>{name}</Text>
                      </Box>
                    ))}
                  </Box>
                </FlexColumn>
              </FlexColumn>
            ) : undefined;
          }

          let confirmBodyContent;
          if (hasMappings) {
            confirmBodyContent =
              'This runbook has alert policy assignments. Are you sure you want to permanently delete it and all its assignments?';
          }
          if (linkedConfigsConfirmBodyContent) {
            confirmBodyContent = linkedConfigsConfirmBodyContent;
          }

          return (
            <RemoveButton
              key="remove"
              model={model}
              showIcon
              hideText
              entityName="runbook"
              small
              popoverProps={{ confirmBodyContent }}
            />
          );
        },
        (model) => (
          <Button
            key="edit"
            icon={canUpdate ? 'edit' : 'search'}
            title={canUpdate ? 'Edit' : 'View'}
            onClick={() => this.handleEdit(model)}
            minimal
            small
          />
        ),
        (model) =>
          !model.isPreset && (
            <Button
              key="history"
              icon="history"
              title="View revisions"
              onClick={() => {
                this.handleShowHistory(model);
              }}
              minimal
              small
            />
          ),
        (model) =>
          canCreate &&
          model.isPreset && (
            <Tooltip key="clone" content="Clone runbook">
              <Button
                icon={FiCopy}
                minimal
                small
                onClick={() => {
                  model.duplicate();
                }}
              />
            </Tooltip>
          )
      ]
    };
    return [
      cellActions,
      {
        label: 'Name',
        name: 'name',
        flexBasis: 20
      },
      {
        label: 'Description',
        name: 'description',
        flexBasis: 15
      },
      {
        label: ' Mappings',
        name: 'mappings',
        flexBasis: 22,
        renderer: ({ model }) => (
          <Flex alignItems="center" gap="3px">
            {Object.entries(model.getMappingCounts()).map(([key, value]) => {
              const mappingsOfType = model.getMappingsOfType(key);
              const policyNames = this.getPolicyNamesForMappings(mappingsOfType, key);

              return (
                <Popover
                  key={key}
                  interactionKind="hover"
                  hoverOpenDelay={300}
                  minimal={false}
                  content={
                    <FlexColumn p={1} gap="3px" minWidth="fit-content" maxWidth={200}>
                      {policyNames.length > 0 ? (
                        policyNames.map((policyItem) => (
                          <Tag
                            key={policyItem.id}
                            interactive
                            onClick={() => {
                              const { history } = this.props;
                              if (key === $runbook.alertPolicyTemplateType && !$auth.isPresetCompany) {
                                history.push(`/v4/alerting/policies/templates?id=${policyItem.id}`);
                              }
                              if (
                                key === $runbook.alertPolicyType ||
                                (key === $runbook.alertPolicyTemplateType && $auth.isPresetCompany)
                              ) {
                                history.push('/v4/alerting/policies', { filters: { id: [policyItem.id] } });
                              }
                            }}
                          >
                            {policyItem.name}
                          </Tag>
                        ))
                      ) : (
                        <Text small muted>
                          No {$runbook.getResourceTypeLabel(key, 0).toLowerCase()} found
                        </Text>
                      )}
                    </FlexColumn>
                  }
                >
                  <Tag>
                    {value} {$runbook.getResourceTypeLabel(key, value)}
                  </Tag>
                </Popover>
              );
            })}
          </Flex>
        )
      },
      {
        label: 'Last Modified',
        name: 'cdate',
        renderer: dateTimeFormatter,
        flexBasis: 20
      }
    ];
  }

  handleRefresh = () => {
    const { $runbook } = this.props;
    $runbook.collection.fetch({ force: true });
  };

  handleAdd = () => {
    const { $runbook } = this.props;
    this.setState({ isAddDialogOpen: true, selectedModel: $runbook.collection.forge() });
  };

  handleEdit = (model) => {
    this.setState({ isEditDialogOpen: true, selectedModel: model });
  };

  handleShowHistory = (model) => {
    this.setState({ isHistoryDialogOpen: true, selectedModel: model });
  };

  handleCloseDialog = () => {
    this.setState({
      isAddDialogOpen: false,
      isEditDialogOpen: false,
      isHistoryDialogOpen: false,
      selectedModel: undefined
    });
  };

  getPolicyNamesForMappings = (mappings, resourceType) => {
    const { $alerting, $runbook } = this.props;

    if (!mappings || mappings.length === 0) {
      return [];
    }

    // Determine which collection to use based on resource type
    let collection = null;
    if (resourceType === $runbook.alertPolicyTemplateType) {
      collection = $alerting.policyTemplateCollection;
    } else if (resourceType === $runbook.alertPolicyType) {
      collection = $alerting.policyCollection;
    }

    return mappings
      .map((mapping) => {
        let policy = null;

        // Check the specific collection for this resource type
        if (collection?.get) {
          const collectionList = collection.get();
          if (Array.isArray(collectionList)) {
            // Try lookup by ruleId first, then fallback to legacy id
            policy =
              collectionList.find((p) => String(p.ruleId) === String(mapping.resource_id)) ||
              collectionList.find((p) => String(p.id) === String(mapping.resource_id));
          }
        }

        const name = policy
          ? (policy.get ? policy.get('name') : policy.name) ||
            `${$runbook.getResourceTypeLabel(resourceType)} ${mapping.resource_id}`
          : `Unknown ${$runbook.getResourceTypeLabel(resourceType)} (${mapping.resource_id})`;

        // Use 'id' field for both templates and regular policies for consistency
        const id = policy ? policy.id : mapping.resource_id;

        return { name, id };
      })
      .filter((item) => item.name && item.name.trim() !== '');
  };

  getFormDialogProps = (isEditDialog = false) => {
    const { $runbook, history } = this.props;
    const { policies, isAddDialogOpen, isEditDialogOpen, selectedModel } = this.state;
    const isPreset = isEditDialog && selectedModel?.isPreset && !$auth.isPresetCompany;
    const templatePolicies = isPreset
      ? this.getPolicyNamesForMappings(
          selectedModel?.getMappingsOfType($runbook.alertPolicyTemplateType) || [],
          $runbook.alertPolicyTemplateType
        )
      : [];

    return {
      isOpen: isEditDialog ? isEditDialogOpen : isAddDialogOpen,
      model: selectedModel,
      policies,
      resourceType: $runbook.policyResourceType,
      readOnly: isPreset,
      templatePolicies,
      onTemplateClick: isPreset
        ? (p) => {
            history.push(`/v4/alerting/policies/templates?id=${p.id}`);
            this.handleCloseDialog();
          }
        : undefined,
      onClose: this.handleCloseDialog
    };
  };

  groupSummaryLookup = ({ groupKey, group }) => {
    const label = groupKey === 'undefined' ? 'Ungrouped' : groupKey;
    const isKentik = groupKey === 'Kentik Runbooks';

    return (
      <Flex alignItems="center" gap={1}>
        {isKentik ? (
          <KentikLogo onlyMark alt="Kentik" style={{ minWidth: 14, maxWidth: 14 }} />
        ) : (
          <Icon icon={FiUsers} iconSize={14} />
        )}
        <Text>{label}</Text>
        <Tag round small>
          {group.length}
        </Tag>
      </Flex>
    );
  };

  get emptyState() {
    const canCreate = this.canCreateRunbooks;

    return (
      <FlexColumn alignItems="center" justifyContent="center" height="100%" p={2}>
        <FlexColumn maxWidth="60%" alignItems="flex-start">
          <Heading level={3}>Create Runbooks to guide AI Advisor investigations</Heading>
          <Text muted>
            Runbooks are Markdown-formatted recipes that define diagnostic steps for specific alert scenarios. Assign
            them to alert policies to ensure consistent, systematic troubleshooting—whether you investigate manually or
            automate with Auto Investigations.
          </Text>
          {canCreate && (
            <NewButton mt={2} fill={false} text="Create Your First Runbook" onClick={this.handleAdd} showIcon={false} />
          )}
        </FlexColumn>
      </FlexColumn>
    );
  }

  render() {
    const { isEditDialogOpen, isHistoryDialogOpen, isAddDialogOpen, selectedModel, searchTerm } = this.state;
    const { $runbook, loading: storeLoading } = this.props;

    if (storeLoading) {
      return (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <Spinner size={40} />
        </Flex>
      );
    }

    const runbookCount = $runbook.collection.unfilteredSize;

    if (runbookCount === 0) {
      return (
        <>
          {this.emptyState}
          {isAddDialogOpen && <RunbookFormDialog {...this.getFormDialogProps()} />}
        </>
      );
    }

    const canCreate = this.canCreateRunbooks;

    return (
      <FlexColumn height="100%" p={4} flex={1}>
        <FlexColumn>
          <SettingsPanelHeading level={2} mb={1}>
            Runbooks
          </SettingsPanelHeading>
          <Text>
            Runbooks provide specific natural instructions to AI Advisor on how to answer user questions or how to
            troubleshoot a certain Alert. Runbooks can be configured by your Organization and assigned to Alerting
            policies.
          </Text>
        </FlexColumn>
        <Flex justifyContent="flex-end" my={2} gap={1} alignItems="center">
          <Search
            collection={$runbook.collection}
            searchTerm={searchTerm}
            placeholder="Search runbooks..."
            autoFocus={false}
            inputProps={{ className: 'round' }}
            flex={1}
            onChange={(value) => {
              this.setState({ searchTerm: value });
              $runbook.collection.filter(value);
            }}
          />
          <Tooltip content="Refresh" position={Position.BOTTOM}>
            <Button
              icon={FiRefreshCcw}
              onClick={this.handleRefresh}
              disabled={$runbook.collection.requestStatus === 'fetching'}
            />
          </Tooltip>
          {canCreate && <NewButton entityName="Runbook" showIcon onClick={this.handleAdd} intent="none" />}
        </Flex>
        <AdminTable
          collection={$runbook.collection}
          columns={this.columns}
          fetchCollectionOnMount={false}
          searchBar={false}
          groupSummaryLookup={this.groupSummaryLookup}
          flexed
        />
        {isAddDialogOpen && <RunbookFormDialog {...this.getFormDialogProps()} />}
        {isEditDialogOpen && <RunbookFormDialog {...this.getFormDialogProps(true)} />}
        {isHistoryDialogOpen && (
          <RunbookHistoryDialog
            isOpen={isHistoryDialogOpen}
            model={selectedModel}
            canRevert={this.canUpdateRunbooks}
            onClose={this.handleCloseDialog}
          />
        )}
      </FlexColumn>
    );
  }
}

/** @type {import('react').ComponentType<any>} */
const RoutedRunbookTable = withRouter(RunbookTable);

export default RoutedRunbookTable;
